import Image from 'next/image';
import React, { useMemo } from 'react';

import { FIXED_GLANCE_KEY } from '@common/constant/courseConstant';
import { useTheme } from '@mui/material';
import { useTranslation } from 'next-i18next';
import { CourseFixedGlanceItem } from 'src/types/courseFixedGlanceItem';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { IconSize } from '@components/atoms/fontAwesomeIcon/constants';
import CourseFixedGlance from '@components/store/product/courseFixedGlance';
import dateUtil from '@helpers/dateUtils';
import useBundleDetail from '@hooks/bundle/useBundleDetail';
import { BundleCountdownDay } from '@redux/bundle/type/BundleDetailType';

const BundleFixedGlanceSection = () => {
  const { t } = useTranslation('store_course');

  const theme = useTheme();

  const {
    videoCount,
    countdownDayType,
    countdownDay,
    totalVideoDuration,
    attachmentFileCount,
    quizCount,
    articleCount,
    certificateCount,
    courseCount,
    isBundleDetailLoading,
  } = useBundleDetail();

  const displayTotalVideoDuration = useMemo(() => {
    const duration = dateUtil.duration(totalVideoDuration, 'seconds');
    const hours = Math.floor(duration.asHours());
    const minutes = duration.minutes();
    const value = [];
    if (hours > 0) {
      value.push(t('course_detail.course_description.course_fixed_glance.total_video.time.hour', { hours }));
    }
    if (minutes > 0) {
      value.push(t('course_detail.course_description.course_fixed_glance.total_video.time.minute', { minutes }));
    }
    return value.join(' ');
  }, [t, totalVideoDuration]);

  const iconSize = IconSize.lg;

  const hasCertificate = true;

  const fixedGlances: CourseFixedGlanceItem[] = [];

  if (courseCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.TOTAL_COURSES,
      icon: <FontAwesomeIcon icon="fa-light fa-books" color={theme.palette.primary.main} size={iconSize} />,
      title: t('bundle_detail.bundle_description.bundle_fixed_glance.total_course', { courseCount }),
      subTitle: t('bundle_detail.bundle_description.bundle_fixed_glance.from_bundle.subtitle'),
    });
  }

  if (videoCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.VIDEO_COUNT,
      icon: <FontAwesomeIcon icon="fa-light fa-circle-play" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.total_video', { total: videoCount }),
      subTitle: displayTotalVideoDuration,
    });
  }

  if (countdownDayType === BundleCountdownDay.LIFETIME) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.LIFE_TIME_LEARN,
      icon: <FontAwesomeIcon icon="fa-light fa-infinity" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.no_expired_to_learn'),
    });
  }

  if (courseCount && [BundleCountdownDay.ALL_EQUAL, BundleCountdownDay.NOT_EQUAL].includes(countdownDayType)) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.LIFE_TIME_LEARN,
      icon: <Image src="/static/icons/glance-limit-time.svg" width="30px" height="35px" alt="limit-time" />,
      title:
        countdownDayType === BundleCountdownDay.ALL_EQUAL
          ? t('bundle_detail.bundle_description.bundle_fixed_glance.available_days_to_learn', { days: countdownDay })
          : t('bundle_detail.bundle_description.bundle_fixed_glance.available_days_from_each_course'),
      subTitle: t('bundle_detail.bundle_description.bundle_fixed_glance.available_days.subtitle'),
    });
  }

  if (attachmentFileCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.ATTACHMENT_FILE_COUNT,
      icon: <FontAwesomeIcon icon="fa-light fa-file-arrow-down" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.attachment_download'),
      subTitle: t('course_detail.course_description.course_fixed_glance.total_attachment_download', {
        total: attachmentFileCount,
      }),
    });
  }

  if (articleCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.ARTICLE_COUNT,
      icon: <FontAwesomeIcon icon="fa-light fa-file-lines" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.article'),
      subTitle: t('course_detail.course_description.course_fixed_glance.total_article', { total: articleCount }),
    });
  }

  if (hasCertificate && certificateCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.CERTIFICATE,
      icon: <Image src="/static/icons/glance-certificate.svg" width="30px" height="35px" alt="certificate" />,
      title: t('bundle_detail.bundle_description.bundle_fixed_glance.total_certificate', {
        certificateCount,
      }),
      subTitle: t('bundle_detail.bundle_description.bundle_fixed_glance.from_bundle.subtitle'),
    });
  }

  if (quizCount) {
    fixedGlances.push({
      id: FIXED_GLANCE_KEY.QUIZ_COUNT,
      icon: <FontAwesomeIcon icon="fa-light fa-circle-question" color={theme.palette.primary.main} size={iconSize} />,
      title: t('course_detail.course_description.course_fixed_glance.quiz'),
      subTitle: t('course_detail.course_description.course_fixed_glance.total_quiz', { total: quizCount }),
    });
  }

  return <CourseFixedGlance isLoading={isBundleDetailLoading} glances={fixedGlances} />;
};

export default BundleFixedGlanceSection;
